import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  FlaskConical, Trophy, FolderKanban, Target, Users, BookOpen,
  ListChecks, GraduationCap, ClipboardList, ArrowRight, CheckCircle2,
} from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="p-5 rounded-md" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
          <p className="font-heading font-extrabold text-3xl mt-1 font-data">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center text-accent-foreground">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function StudentDashboard() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["dash-student"], queryFn: () => api.get("/dashboard/student").then((r) => r.data) });
  const d = data || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl">Halo, {user.name.split(" ")[0]} 👋</h1>
        <p className="text-muted-foreground mt-1">Lanjutkan belajar dan jalankan eksperimen HPLC Anda.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={FlaskConical} label="Eksperimen" value={d.experiments_completed ?? "–"} sub="total dijalankan" testid="stat-experiments" />
        <Stat icon={Target} label="Skor Terakhir" value={d.latest_score ?? "–"} sub="eksperimen terbaru" testid="stat-latest" />
        <Stat icon={Trophy} label="Skor Terbaik" value={d.best_score ?? "–"} sub="pencapaian tertinggi" testid="stat-best" />
        <Stat icon={FolderKanban} label="Peringkat" value={d.my_rank ? `#${d.my_rank}` : "–"} sub={`dari ${d.total_students ?? 0} mahasiswa`} testid="stat-rank" />
      </div>

      <ScenarioScorecard />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 rounded-md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg">Mulai Cepat</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <QuickLink to="/lab" icon={FlaskConical} title="Lab Virtual" desc="Rancang & jalankan eksperimen" />
            <QuickLink to="/scenarios" icon={ListChecks} title="Skenario" desc="Praktikum terarah" />
            <QuickLink to="/pjbl" icon={FolderKanban} title="PjBL" desc="Proyek & pengumpulan" />
            <QuickLink to="/materials" icon={BookOpen} title="Materi" desc="Pelajari teori HPLC" />
            <QuickLink to="/leaderboard" icon={Trophy} title="Papan Skor" desc="Lihat peringkat kelas" />
          </div>
        </Card>

        <Card className="p-6 rounded-md">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-5 w-5 text-primary" />
            <h2 className="font-heading font-bold text-lg">Papan Skor</h2>
          </div>
          <div className="space-y-2">
            {(d.leaderboard_snapshot || []).map((b) => (
              <div key={b.user_id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div className="flex items-center gap-3">
                  <span className="font-data text-sm w-6 text-muted-foreground">#{b.rank}</span>
                  <span className="text-sm font-medium truncate max-w-[120px]">{b.name}</span>
                </div>
                <span className="font-data font-semibold text-sm">{b.total}</span>
              </div>
            ))}
            {(!d.leaderboard_snapshot || d.leaderboard_snapshot.length === 0) && (
              <p className="text-sm text-muted-foreground">Belum ada data.</p>
            )}
          </div>
          <Button asChild variant="ghost" size="sm" className="w-full mt-3">
            <Link to="/leaderboard">Lihat selengkapnya <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </Card>
      </div>
    </div>
  );
}

function ScenarioScorecard() {
  const { data: scores = [] } = useQuery({ queryKey: ["scenario-scores"], queryFn: () => api.get("/dashboard/scenario-scores").then((r) => r.data) });
  if (scores.length === 0) return null;
  return (
    <Card className="p-6 rounded-md" data-testid="scenario-scorecard">
      <div className="flex items-center gap-2 mb-4">
        <Target className="h-5 w-5 text-primary" />
        <h2 className="font-heading font-bold text-lg">Kartu Skor Skenario</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {scores.map((s) => {
          const t = s.target || {};
          const targetText = [
            t.min_resolution ? `Rs ≥ ${t.min_resolution}` : null,
            t.max_runtime_min ? `≤ ${t.max_runtime_min} mnt` : null,
            t.required_count ? `${t.required_count} analit` : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={s.scenario_id} className="border border-border rounded-md p-4" data-testid={`scorecard-${s.scenario_id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-semibold text-sm">{s.title}</div>
                  <div className="text-xs text-muted-foreground font-data">Target: {targetText || "—"}</div>
                </div>
                {s.achieved
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1 flex-shrink-0"><CheckCircle2 className="h-3 w-3" /> Tercapai</Badge>
                  : s.attempts > 0
                    ? <Badge variant="outline" className="flex-shrink-0">Dicoba</Badge>
                    : <Badge variant="outline" className="flex-shrink-0 text-muted-foreground">Belum</Badge>}
              </div>
              <div className="flex items-center gap-3">
                <Progress value={s.best_score ?? 0} className="h-2 flex-1" />
                <span className="font-data font-semibold text-sm w-14 text-right">
                  {s.best_score != null ? s.best_score : "–"}
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 font-data">
                {s.attempts > 0 ? `${s.attempts} percobaan · skor terbaik` : "Belum ada percobaan"}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link
      to={to}
      data-testid={`quick-${to.replace(/\//g, "")}`}
      className="group border border-border rounded-md p-4 hover:border-primary hover:bg-accent/40 transition-colors"
    >
      <Icon className="h-5 w-5 text-primary mb-2" />
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-muted-foreground">{desc}</div>
    </Link>
  );
}

function LecturerDashboard() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["dash-lecturer"], queryFn: () => api.get("/dashboard/lecturer").then((r) => r.data) });
  const d = data || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-extrabold text-3xl">Dasbor {user.role === "admin" ? "Admin" : "Dosen"}</h1>
        <p className="text-muted-foreground mt-1">Pantau aktivitas kelas dan kelola konten pembelajaran.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Stat icon={Users} label="Mahasiswa" value={d.students ?? "–"} testid="stat-students" />
        <Stat icon={BookOpen} label="Materi" value={d.materials ?? "–"} testid="stat-materials" />
        <Stat icon={ListChecks} label="Skenario" value={d.scenarios ?? "–"} testid="stat-scenarios" />
        <Stat icon={FolderKanban} label="Proyek PjBL" value={d.projects ?? "–"} testid="stat-projects" />
        <Stat icon={FlaskConical} label="Eksperimen" value={d.total_experiments ?? "–"} sub="oleh mahasiswa" testid="stat-total-exp" />
        <Stat icon={ClipboardList} label="Menunggu Nilai" value={d.pending_grading ?? "–"} sub="submission PjBL" testid="stat-pending" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 rounded-md">
          <h2 className="font-heading font-bold text-lg mb-4">Aksi Cepat</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <QuickLink to="/lecturer/content" icon={BookOpen} title="Kelola Konten" desc="Materi & skenario" />
            <QuickLink to="/lecturer/grading" icon={GraduationCap} title="Penilaian PjBL" desc="Nilai submission" />
            <QuickLink to="/lab" icon={FlaskConical} title="Lab Virtual" desc="Coba simulasi" />
            {user.role === "admin" && <QuickLink to="/admin" icon={Users} title="Admin" desc="Pengguna & data" />}
          </div>
        </Card>

        <Card className="p-6 rounded-md">
          <h2 className="font-heading font-bold text-lg mb-4">Submission Terbaru (perlu dinilai)</h2>
          <div className="space-y-2">
            {(d.recent_submissions || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                <div>
                  <div className="text-sm font-medium">{s.user_name}</div>
                  <div className="text-xs text-muted-foreground font-data">{s.nim}</div>
                </div>
                <Badge variant="outline">Terkirim</Badge>
              </div>
            ))}
            {(!d.recent_submissions || d.recent_submissions.length === 0) && (
              <p className="text-sm text-muted-foreground">Tidak ada submission menunggu.</p>
            )}
          </div>
          <Button asChild variant="ghost" size="sm" className="w-full mt-3">
            <Link to="/lecturer/grading">Ke penilaian <ArrowRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user.role === "student") return <StudentDashboard />;
  return <LecturerDashboard />;
}
